import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import UserImg from '../../assets/images/user.png';
import GenderImg from '../../assets/images/gender.png';
import FiPhoneImg from '../../assets/images/fi_phone.png';
import EmailImg from '../../assets/images/email.png';
import axiosInstance from '../../api/axiosInstance';
import BackArrow from '../../assets/images/Frame_18177.png';
import InactivityTimer from '../../components/InactivityTimer';
import {
    CalendarDays,
    CalendarPlus,
    CircleHelp,
    Files,
    MessageSquareHeart,
    WalletCards,
} from 'lucide-react';

const Dashboard = () => {
    const navigate = useNavigate();
    const [patientData, setPatientData] = useState({});
    const [showPatientDetails, setShowPatientDetails] = useState(false);

    // Function to calculate age from DOB
    const calculateAge = (dob) => {
        if (!dob) return { years: 0, months: 0, days: 0 };

        try {
            const dobDate = new Date(dob);

            const today = new Date();

            // Validate DOB
            if (isNaN(dobDate.getTime()) || dobDate > today) {
                return { years: 0, months: 0, days: 0 };
            }

            let years = today.getFullYear() - dobDate.getFullYear();
            let months = today.getMonth() - dobDate.getMonth();
            let days = today.getDate() - dobDate.getDate();

            // Adjust for negative days
            if (days < 0) {
                months -= 1;
                const lastMonth = new Date(today.getFullYear(), today.getMonth(), 0);
                days += lastMonth.getDate();
            }

            // Adjust for negative months
            if (months < 0) {
                years -= 1;
                months += 12;
            }

            // Ensure years is not negative
            if (years < 0) {
                return { years: 0, months: 0, days: 0 };
            }

            return { years, months, days };
        } catch (error) {
            console.error('Error calculating age:', error);
            return { years: 0, months: 0, days: 0 };
        }
    };

    const fetchPatientData = async () => {
        try {
            const response = await axiosInstance.get(`api/patient/details`);
            const rawData = response.data.patient || {};

            // Map API response to expected format
            const data = {
                patientName: `${rawData.prefix || ''} ${rawData.firstName || ''} ${rawData.middleName || ''} ${rawData.lastName || ''}`.replace(/\s+/g, ' ').trim(),
                mrno: rawData.mrno || 'N/A',
                gender: rawData.genderCode === 'M' ? 'Male' : rawData.genderCode === 'F' ? 'Female' : 'Other',
                dob: rawData.dob || null,
                age: rawData.age || null,
                createdAt: rawData.createdAt || null,
                patientType: rawData.externalPatientId ? 'HIMS Registered' : 'Local',
                aadharNumber: rawData.aadharNumber || 'N/A',
                mobileNo: rawData.contactNumber || 'N/A',
                email: rawData.emailAddress || 'N/A',
                village: rawData.address || 'N/A',
                pin: rawData.pinCode || 'N/A',
                locality: rawData.locality || 'N/A',
                district: rawData.district || 'N/A',
                city: rawData.city || 'N/A',
                postOffice: rawData.postOffice || 'N/A',
                state: rawData.state || 'N/A',
                policeStation: rawData.policeStation || 'N/A',
                country: rawData.nationality || 'N/A',
                patientImg: rawData.base64String ? `data:image/jpeg;base64,${rawData.base64String}` : null,
            };

            // Calculate age if DOB exists
            if (data.dob) {
                const { years, months, days } = calculateAge(data.dob);
                data.years = years;
                data.months = months;
                data.days = days;
            }

            setPatientData(data);
        } catch (error) {
            console.error('Error while fetching patient data:', error);
            setPatientData({});
        }
    };

    useEffect(() => {
        fetchPatientData();
    }, []);

    const handleLogout = () => {
        localStorage.removeItem('kiosk_token');
        localStorage.removeItem('patient_profile');
        navigate("/", { replace: true });
    };

    const patientServices = [
        {
            to: '/opd-schedule',
            title: 'OPD Calendar',
            description: 'View department-wise doctor availability.',
            icon: CalendarDays,
        },
        {
            to: '/online-deposit',
            title: 'Advance Payment',
            description: 'Make an advance deposit securely.',
            icon: WalletCards,
        },
        {
            to: '/book-appointment',
            title: 'Book Appointment',
            description: 'Book a new appointment with a doctor.',
            icon: CalendarPlus,
        },
        {
            to: '/reports-bills',
            title: 'Reports & Bill',
            description: 'Access lab, radiology reports and bills.',
            icon: Files,
        },
        {
            to: '/feedback',
            title: 'Feedback',
            description: 'Share your hospital experience with us.',
            icon: MessageSquareHeart,
        },
        {
            to: '/faqs',
            title: 'FAQs',
            description: 'Find answers to frequently asked questions.',
            icon: CircleHelp,
        },
    ];

    return (
        <>
            <InactivityTimer />
            <div className="min-h-screen overflow-x-hidden">
                <div className="flex flex-wrap items-center gap-3 px-4 sm:px-8 lg:px-14 pt-4 sm:pt-6">
                    <Link
                        to="/"
                        className="flex w-auto min-w-[7rem] px-4 py-2 sm:py-3 bg-[#0FA968] text-white font-semibold rounded-lg shadow hover:bg-[#138F5E] transition duration-200"
                        style={{ fontFamily: 'Roboto', fontSize: '20px', fontWeight: '500', lineHeight: '23.44px' }}
                    >
                        <img src={BackArrow} alt="Back" className="mr-2 w-5 h-5 sm:w-6 sm:h-6" />
                        Back
                    </Link>
                    <span className="text-xl sm:text-2xl lg:text-[28px] font-semibold" style={{ fontFamily: 'Roboto', fontWeight: '600', lineHeight: '1.2' }}>
                        Dashboard
                    </span>
                </div>

                <hr className="mt-4 sm:mt-6 mx-4 sm:mx-8 lg:mx-14 border-t border-gray-300" />

                <div className="page-container flex flex-col">
                    <div className="form-container flex-grow overflow-auto overflow-x-hidden">
                        <div className="px-4 sm:px-8 lg:px-14 mt-4 sm:mt-6 max-w-[1600px] mx-auto w-full">
                            <div className="grid grid-cols-1 lg:grid-cols-[150px_2fr_3fr] xl:grid-cols-[150px_2fr_3fr] gap-4 md:gap-6 bg-[#42424A] p-4 sm:p-5 rounded-lg">
                                <div className="flex justify-center lg:justify-start">
                                    {patientData.patientImg && (
                                        <img
                                            src={patientData.patientImg}
                                            alt="Patient"
                                            className="rounded-xl w-[120px] h-[130px] sm:w-[148px] sm:h-[160px] object-cover"
                                        />
                                    )}
                                    {!patientData.patientImg && (
                                        <img
                                            src={UserImg}
                                            alt="Patient"
                                            className="rounded-xl p-2 bg-[#d3d3d3] w-[120px] h-[130px] sm:w-[148px] sm:h-[160px] object-cover"
                                        />
                                    )}
                                </div>
                                <div className={`font-roboto min-w-0 pb-4 lg:pb-0 lg:pr-4 ${showPatientDetails ? 'lg:border-r lg:border-[#757582]' : ''}`}>
                                    <div className="flex flex-col sm:flex-row sm:flex-wrap sm:items-center gap-2 sm:gap-4">
                                        <span className="text-white text-lg sm:text-[22px] font-semibold break-words">
                                            {patientData.patientName}
                                        </span>

                                        <span className="inline-flex items-center self-start p-1 bg-[#565661] text-white rounded-xl text-sm sm:text-base">
                                            <img src={GenderImg} alt="Gender" className="mr-1 h-4 w-4" />
                                            {patientData.gender}
                                        </span>
                                    </div>
                                    <div className="mt-1 mb-2">
                                        <span className="text-white text-sm sm:text-[14px]">MRN: </span>
                                        <span className="text-white text-base sm:text-[16px] font-semibold">{patientData.mrno}</span>
                                    </div>
                                    <div className="font-roboto">
                                        <div className="grid grid-cols-1 sm:grid-cols-[2fr_3fr] text-white text-sm sm:text-[14px] gap-1 sm:gap-4 mt-2">
                                            <div>
                                                <h3>Age:</h3>
                                            </div>
                                            <div className="break-words">
                                                {patientData.dob ? (
                                                    <span>
                                                        {patientData.years > 0 && `${patientData.years} years `}
                                                        {patientData.months > 0 && `${patientData.months} months `}
                                                        {`${patientData.days || 0} days `}
                                                        {"(" + patientData.dob + ")"}
                                                    </span>
                                                ) : (
                                                    patientData.age && (
                                                        <span>
                                                            {patientData.age}
                                                        </span>
                                                    )
                                                )}
                                            </div>
                                        </div>
                                    </div>

                                    {!showPatientDetails ? (
                                        <button
                                            type="button"
                                            onClick={() => setShowPatientDetails(true)}
                                            className="mt-4 px-4 py-2 bg-[#0FA968] text-white text-sm sm:text-base font-semibold rounded-lg hover:bg-[#138F5E] transition duration-200"
                                        >
                                            View Details
                                        </button>
                                    ) : (
                                        <>
                                            <div className="font-roboto">
                                                <div className="grid grid-cols-1 sm:grid-cols-[2fr_3fr] text-white text-sm sm:text-[14px] gap-1 sm:gap-4 mt-2">
                                                    <div>
                                                        <h3>Registered on:</h3>
                                                    </div>
                                                    <div>
                                                        <span className="flex flex-col sm:flex-row sm:flex-wrap sm:items-center gap-2">
                                                            {patientData.createdAt && new Date(patientData.createdAt).toLocaleString('en-GB', {
                                                                day: '2-digit',
                                                                month: '2-digit',
                                                                year: 'numeric',
                                                                hour: '2-digit',
                                                                minute: '2-digit',
                                                                second: '2-digit',
                                                                hour12: false
                                                            })}
                                                            <span className="inline-flex items-center self-start px-2 py-0.5 bg-[#0FA968] text-white rounded-xl text-xs sm:text-[14px]">{patientData.patientType}</span>
                                                        </span>
                                                    </div>
                                                </div>
                                                <div className="grid grid-cols-1 sm:grid-cols-[2fr_3fr] text-white text-sm sm:text-[14px] gap-1 sm:gap-4 mt-2">
                                                    <div>
                                                        <h3>Aadhar Number:</h3>
                                                    </div>
                                                    <div className="break-words">
                                                        <span>{patientData.aadharNumber}</span>
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="font-roboto">
                                                <div className="grid grid-cols-[16px_auto] text-white text-sm sm:text-[14px] gap-2 mt-2">
                                                    <div>
                                                        <img src={FiPhoneImg} alt="Phone" />
                                                    </div>
                                                    <div className="break-words">
                                                        <span>{patientData.mobileNo}</span>
                                                    </div>
                                                </div>
                                                <div className="grid grid-cols-[16px_auto] text-white text-sm sm:text-[14px] gap-2 mt-2">
                                                    <div>
                                                        <img src={EmailImg} alt="Email" />
                                                    </div>
                                                    <div className="break-all">
                                                        <span>{patientData.email}</span>
                                                    </div>
                                                </div>
                                            </div>
                                        </>
                                    )}
                                </div>
                                {showPatientDetails && (
                                <div className="font-roboto min-w-0 pt-4 lg:pt-0 border-t lg:border-t-0 border-[#757582]">
                                    <h1 className="text-sm sm:text-[16px] text-[#92929E] font-semibold">ADDRESS DETAILS</h1>
                                    <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 text-white text-sm sm:text-[14px] gap-3 sm:gap-4 mb-2">
                                        <div>
                                            <div className="grid grid-cols-[100px_1fr] gap-2">
                                                <div>
                                                    <h3 className="text-sm sm:text-[14px]">Address:</h3>
                                                </div>
                                                <div>
                                                    <span className="text-sm sm:text-[14px] break-words">{patientData.village}</span>
                                                </div>
                                            </div>
                                        </div>
                                        <div>
                                            <div className="grid grid-cols-[100px_1fr] sm:grid-cols-2 gap-2">
                                                <div>
                                                    <h3>Pin Code:</h3>
                                                </div>
                                                <div>
                                                    <span>{patientData.pin}</span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="grid grid-cols-1 sm:grid-cols-2 text-white text-sm sm:text-[14px] gap-3 sm:gap-4 mb-2">
                                        <div>
                                            <div className="grid grid-cols-[100px_1fr] gap-2">
                                                <div>
                                                    <h3 className="text-sm sm:text-[14px]">Locality:</h3>
                                                </div>
                                                <div>
                                                    <span className="text-sm sm:text-[14px] break-words">{patientData.locality}</span>
                                                </div>
                                            </div>
                                        </div>
                                        <div>
                                            <div className="grid grid-cols-[100px_1fr] sm:grid-cols-2 gap-2">
                                                <div>
                                                    <h3>City:</h3>
                                                </div>
                                                <div>
                                                    <span>{patientData.city}</span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="grid grid-cols-1 sm:grid-cols-2 text-white text-sm sm:text-[14px] gap-3 sm:gap-4 mb-2">
                                        <div>
                                            <div className="grid grid-cols-[100px_1fr] gap-2">
                                                <div>
                                                    <h3 className="text-sm sm:text-[14px]">Post Office:</h3>
                                                </div>
                                                <div>
                                                    <span className="text-sm sm:text-[14px] break-words">{patientData.postOffice}</span>
                                                </div>
                                            </div>
                                        </div>
                                        <div>
                                            <div className="grid grid-cols-[100px_1fr] sm:grid-cols-2 gap-2">
                                                <div>
                                                    <h3>District:</h3>
                                                </div>
                                                <div>
                                                    <span>{patientData.district}</span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="grid grid-cols-1 sm:grid-cols-2 text-white text-sm sm:text-[14px] gap-3 sm:gap-4 mb-2">
                                        <div>
                                            <div className="grid grid-cols-[100px_1fr] gap-2">
                                                <div>
                                                    <h3 className="text-sm sm:text-[14px]">Police Station:</h3>
                                                </div>
                                                <div>
                                                    <span className="text-sm sm:text-[14px] break-words">{patientData.policeStation}</span>
                                                </div>
                                            </div>
                                        </div>
                                        <div>
                                            <div className="grid grid-cols-[100px_1fr] sm:grid-cols-2 gap-2">
                                                <div>
                                                    <h3>State:</h3>
                                                </div>
                                                <div>
                                                    <span>{patientData.state}</span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="grid grid-cols-1 sm:grid-cols-2 text-white text-sm sm:text-[14px] gap-3 sm:gap-4 mb-2">
                                        <div className="hidden sm:block" />
                                        <div>
                                            <div className="grid grid-cols-[100px_1fr] sm:grid-cols-2 gap-2">
                                                <div>
                                                    <h3>Country:</h3>
                                                </div>
                                                <div>
                                                    <span>{patientData.country}</span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                )}
                                {showPatientDetails && (
                                    <div className="lg:col-span-3 flex justify-center sm:justify-start">
                                        <button
                                            type="button"
                                            onClick={() => setShowPatientDetails(false)}
                                            className="px-4 py-2 bg-[#565661] text-white text-sm sm:text-base font-semibold rounded-lg hover:bg-[#45454f] transition duration-200"
                                        >
                                            Hide
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>
                        <div className="px-4 sm:px-8 lg:px-14 mt-4 sm:mt-6 mb-6 max-w-[1600px] mx-auto w-full">
                            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
                                {patientServices.map(({ to, title, description, icon: Icon }) => (
                                    <Link
                                        key={to}
                                        to={to}
                                        state={{ patientName: patientData.patientName }}
                                        className="group flex h-full flex-col items-center rounded-xl border border-gray-100 bg-white p-5 text-center shadow-sm transition duration-300 hover:-translate-y-1 hover:shadow-lg"
                                    >
                                        <div className="mb-4 inline-flex rounded-2xl bg-emerald-50 p-4 text-[#0FA968] transition group-hover:bg-[#0FA968] group-hover:text-white">
                                            <Icon className="h-12 w-12" />
                                        </div>
                                        <p className="font-roboto text-xl font-medium text-[#27272A] sm:text-2xl">
                                            {title}
                                        </p>
                                        <p className="mt-2 font-roboto text-sm text-[#565661] sm:text-base">
                                            {description}
                                        </p>
                                    </Link>
                                ))}
                            </div>

                        {/* Close Session Button */}
                        <div className="flex justify-center mt-6 px-2">
                            <button
                                onClick={handleLogout}
                                className="w-full sm:w-auto px-6 py-3 text-white bg-red-600 hover:bg-red-700 rounded-lg font-semibold text-base sm:text-[18px] shadow-lg hover:shadow-xl transition-all duration-200 flex items-center justify-center gap-2"
                                style={{ fontFamily: 'Roboto' }}
                            >
                                <svg
                                    className="w-5 h-5"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
                                    />
                                </svg>
                                Close Session
                            </button>
                        </div>
                    </div>
                </div>
            </div>
            </div>
        </>
    );
};

export default Dashboard;